import { FC, useMemo } from 'react';
import Image from 'next/image';
import { useGlassesStore } from 'zus/glassesStore';
import { useQueryState } from 'nuqs';
import { titleCase } from 'functions/capitalize';
import { useRouter } from 'next/router';
import { frameShapeToName } from 'functions/frameShapeToName';

const ShapeSwitcher: FC<{
  frameArmsOptions: any;
}> = ({ frameArmsOptions }) => {
  const router = useRouter();
  const setFrameShape = useGlassesStore((state) => state.setFrameShape);
  const [frameShapeQuery, setFrameShapeQuery] = useQueryState('shape');
  const frameShape = frameShapeQuery ?? 'classic';
  const currentFrameColor = router.query.product as string;

  const category = useMemo(() => {
    return router.asPath
      ?.split('/')
      ?.find((segment, index) => index === 1 && segment === 'shop')
      ? router.asPath.split('/')[2]
      : 'prescription-glasses';
  }, [router.asPath]);

  const availableShapes = useMemo(() => {
    if (!frameArmsOptions) return {};
    return frameArmsOptions.frame_options.reduce((acc: any, option: any) => {
      const hasCurrentColor = option.color_size.some((cs: any) => cs.slug === currentFrameColor);
      acc[option.shape.toLowerCase()] = hasCurrentColor;
      return acc;
    }, {});
  }, [frameArmsOptions, currentFrameColor]);

  const handleShapeChange = (shape: string) => {
    if (availableShapes[shape.toLowerCase()]) {
      setFrameShape(shape);
      if (shape === 'classic') {
        setFrameShapeQuery(null);
      } else {
        setFrameShapeQuery(shape.toLowerCase());
      }
    } else {
      const selectedShapeOption = frameArmsOptions.frame_options
        .find((option: any) => option.shape.toLowerCase() === shape.toLowerCase());

      if (selectedShapeOption?.color_size?.length > 0) {
        const firstAvailableProduct = selectedShapeOption.color_size[0].slug;
        if (firstAvailableProduct && shape === 'round') {
          router.push(`/shop/${category}/${firstAvailableProduct}?shape=${shape.toLowerCase()}`);
        }
      }
    }
  };

  return (
    <div className="mb-6">
      <h5 className="text-black font-medium text-[0.8rem] font-sans mb-4">
        Frame Shape, <span className="text-[#959595]">{frameShapeToName(frameShape)}</span>
      </h5>
      <div className="flex space-x-2">
        <ShapeButton
          shape="classic"
          imagePath="https://w.dresden.vision/images/icons/shape_classic.png"
          isSelected={frameShape.toLowerCase() === 'classic'}
          onClick={() => handleShapeChange('classic')}
          isDisabled={false}
        />
        <ShapeButton
          shape="round"
          imagePath="https://w.dresden.vision/images/icons/shape_round.png"
          isSelected={frameShape.toLowerCase() === 'round'}
          onClick={() => handleShapeChange('round')}
          isDisabled={false}
        />
      </div>
    </div>
  );
};

const ShapeButton: FC<{
  shape: string;
  imagePath: string;
  isSelected: boolean;
  onClick: () => void;
  isDisabled: boolean;
}> = ({ shape, imagePath, isSelected, onClick, isDisabled }) => {
  return (
    <button
      className={`w-[106px] h-[48px] border rounded-md flex flex-col items-center justify-center disable-size ${isSelected ? 'border-black' : 'border-[#CACACA] hover:border-[#AEADAD]'
        } ${isDisabled ? 'opacity-50 cursor-not-allowed ' : ''}`}
      onClick={onClick}
      disabled={isDisabled}
    >
      <Image
        src={imagePath}
        alt={`${shape} shape`}
        width={80}
        height={40}
      />
      {isDisabled && <div className="hr-for-frame !bg-[#CACACA] border-black "></div>}
    </button>
  );
};

export default ShapeSwitcher;
